import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { myExpariencesService } from "../../../services";
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import {
  updateLayout,
  updateModalManager,
  updateMultiUpload,
  updateSelectedTile,
} from "../../../reducers/configurator/configuratorSlice";
import ConfigPredefinedFormatsForm from "./ConfigPredifinedFormatsForm";
import { ExpandMore, PhotoLibrary } from "@mui/icons-material";
import { AudioPlayerControlSprite, Audio } from "react-audio-player-pro";
import "react-audio-player-pro/dist/style.css";

import GoldenSwitch from "../../goldenSwitch/GoldenSwitch";
import { useViewport } from "../../../helpers";

const PREFIX = "ConfigTopOptions";

const classes = {
  root: `${PREFIX}-root`,
  firstLineButtons: `${PREFIX}-firstLineButtons`,
  avatarisationButton: `${PREFIX}-avatarisationButton`,
  marginRight: `${PREFIX}-marginRight`,
  topBarContainer: `${PREFIX}-topBarContainer`,
  audioPlayer: `${PREFIX}-audioPlayer`,
  ambiantMusicAccordion: `${PREFIX}-ambiantMusicAccordion`,
  optionsAccordion: `${PREFIX}-optionsAccordion`,
  heading: `${PREFIX}-heading`,
  secondaryHeading: `${PREFIX}-secondaryHeading`,
  downloadLink: `${PREFIX}-downloadLink`,
  appearanceContainer: `${PREFIX}-appearanceContainer`,
  modifySelect: `${PREFIX}-modifySelect`,
};

const ConfigTopOptionsContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      width: "100%",
      padding: "0 1rem",
      [theme.breakpoints.down("sm")]: {
        marginBottom: "1rem",
      },
    },
    [`& .${classes.firstLineButtons}`]: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      marginBottom: "1rem",
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
      },
    },
    [`& .${classes.avatarisationButton}`]: {
      [theme.breakpoints.down("sm")]: {
        width: "100%",
      },
    },
    [`& .${classes.marginRight}`]: {
      marginRight: "1rem",
      [theme.breakpoints.down("sm")]: {
        marginBottom: "1rem",
        marginRight: "0",
        width: "100%",
      },
    },
    [`& .${classes.topBarContainer}`]: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-around",
      margin: "0 0 1rem 0",
      [theme.breakpoints.between("xs", "sm")]: {
        flexDirection: "column",
        margin: "0",
      },
    },
    [`& .${classes.audioPlayer}`]: {
      [theme.breakpoints.between("xs", "sm")]: {
        width: "100%",
      },
      width: "450px",
    },
    [`& .${classes.ambiantMusicAccordion}`]: {
      backgroundColor: "#424242",
    },
    [`& .${classes.optionsAccordion}`]: {
      backgroundColor: "#424242",
    },
    [`& .${classes.heading}`]: {
      fontSize: theme.typography.pxToRem(15),
      fontWeight: theme.typography.fontWeightRegular,
      flexBasis: "50%",
    },
    [`& .${classes.secondaryHeading}`]: {
      fontSize: theme.typography.pxToRem(15),
      color: theme.palette.text.secondary,
      flexBasis: "50%",
      maxWidth: "170px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      transform: "translate(10px, 8px)",
      [theme.breakpoints.between("xs", "sm")]: {
        transform: "translate(10px, 10px)",
        maxWidth: "150px",
      },
    },
    [`& .${classes.downloadLink}`]: {
      textDecoration: "none",
      marginRight: "1rem",
    },
    [`& .${classes.appearanceContainer}`]: {
      display: "flex",
      width: "300px",
      justifyContent: "space-around",
      [theme.breakpoints.between("xs", "sm")]: {
        marginTop: "1rem",
        width: "100%",
      },
    },
    [`& .${classes.modifySelect}`]: {
      width: "100%",
    },
  };
});

const ConfigTopOptions = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { width } = useViewport();
  const [disableAvatar, setDisableAvatar] = useState(false);
  const [isHandlingSong, setIsHandlingSong] = useState(false);
  const [isMusicExpanded, setIsMusicExpanded] = useState(false);

  const isAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.role === "ADMIN"
  );
  const isMultiSelection = useSelector(
    (state) => state.configurator.multiSelection.active
  );
  const isMultiUpload = useSelector(
    (state) => state.configurator.multiUpload.active
  );

  const appearances = [
    { name: t("configurator.appearances.none"), value: "none" },
    { name: t("configurator.appearances.bottomToTop"), value: "bottomToTop" },
    { name: t("configurator.appearances.topToBottom"), value: "topToBottom" },
    { name: t("configurator.appearances.backToFront"), value: "backToFront" },
    { name: t("configurator.appearances.frontToBack"), value: "frontToBack" },
    { name: t("configurator.appearances.dissolve"), value: "dissolve" },
  ];

  const handleAudioName = (url) => {
    if (url.length > 0) {
      if (url.includes("defaultADSSong.mp3"))
        return t("configurator.defaultMusic");
      let tmp = url.substr(8, url.length).split("/");
      return tmp[tmp.length - 1];
    } else return "";
  };

  return (
    <ConfigTopOptionsContainer sx={{ width: "100%" }}>
      <div className={classes.root}>
        <div className={classes.firstLineButtons}>
          {width < 600 ? (
            <Accordion className={classes.optionsAccordion}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel-ambiantMusic"
                id="panel-ambiantMusic-header"
              >
                <Typography variant="body1">
                  {t("configurator.options")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {isAdmin && (
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={isMultiSelection || isMultiUpload}
                    onClick={() =>
                      dispatch(
                        updateModalManager({
                          open: true,
                          type: "backdrop",
                          renderer: <ConfigPredefinedFormatsForm />,
                          title: t("configurator.presetButton"),
                        })
                      )
                    }
                    className={classes.marginRight}
                  >
                    {t("configurator.presetButton")}
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  component="label"
                  className={classes.marginRight}
                  disabled={isMultiSelection || isMultiUpload}
                  startIcon={<PhotoLibrary />}
                  onChange={(e) => {
                    dispatch(updateLayout("normal"));
                    dispatch(updateSelectedTile(null));
                    dispatch(
                      updateMultiUpload({
                        active: true,
                        files: Array.from(e.target.files),
                      })
                    );
                  }}
                >
                  {t("configurator.multiUpload")}
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    name="multiUpload"
                    hidden
                    onClick={(e) => (e.target.value = null)}
                  />
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.avatarisationButton}
                  onClick={() => {
                    setDisableAvatar(true);
                    myExpariencesService
                      .updateAvatarisation(props.exparience._id, isAdmin)
                      .then(() => {
                        props.setExparience({
                          ...props.exparience,
                          avatar: !props.exparience.avatar,
                        });
                      })
                      .catch((err) => {
                        if (err === 403)
                          enqueueSnackbar(t("toasts.unauthorizedError"), {
                            variant: "error",
                          });
                        else
                          enqueueSnackbar(t("toasts.toggleAmbiantMusicError"), {
                            variant: "error",
                          });
                      })
                      .finally(() => setDisableAvatar(false));
                  }}
                  disabled={disableAvatar || isMultiSelection || isMultiUpload}
                >
                  {props.exparience.avatar
                    ? t("configurator.avatarisationActivated")
                    : t("configurator.avatarisationDesactivated")}
                </Button>
              </AccordionDetails>
            </Accordion>
          ) : (
            <>
              {isAdmin && (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isMultiSelection || isMultiUpload}
                  onClick={() =>
                    dispatch(
                      updateModalManager({
                        open: true,
                        type: "backdrop",
                        renderer: <ConfigPredefinedFormatsForm />,
                        title: t("configurator.presetButton"),
                      })
                    )
                  }
                  className={classes.marginRight}
                >
                  {t("configurator.presetButton")}
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                component="label"
                className={classes.marginRight}
                disabled={isMultiSelection || isMultiUpload}
                startIcon={<PhotoLibrary />}
                onChange={(e) => {
                  dispatch(updateLayout("normal"));
                  dispatch(updateSelectedTile(null));
                  dispatch(
                    updateMultiUpload({
                      active: true,
                      files: Array.from(e.target.files),
                    })
                  );
                }}
              >
                {t("configurator.multiUpload")}
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  name="multiUpload"
                  hidden
                  onClick={(e) => (e.target.value = null)}
                />
              </Button>
              <Button
                variant="contained"
                color="primary"
                className={classes.avatarisationButton}
                onClick={() => {
                  setDisableAvatar(true);
                  myExpariencesService
                    .updateAvatarisation(props.exparience._id, isAdmin)
                    .then(() => {
                      props.setExparience({
                        ...props.exparience,
                        avatar: !props.exparience.avatar,
                      });
                    })
                    .catch((err) => {
                      if (err === 403)
                        enqueueSnackbar(t("toasts.unauthorizedError"), {
                          variant: "error",
                        });
                      else
                        enqueueSnackbar(t("toasts.toggleAmbiantMusicError"), {
                          variant: "error",
                        });
                    })
                    .finally(() => setDisableAvatar(false));
                }}
                disabled={disableAvatar || isMultiSelection || isMultiUpload}
              >
                {props.exparience.avatar
                  ? t("configurator.avatarisationActivated")
                  : t("configurator.avatarisationDesactivated")}
              </Button>
            </>
          )}
        </div>
        <div className={classes.topBarContainer}>
          <div className={classes.audioPlayer}>
            <Accordion
              className={classes.ambiantMusicAccordion}
              expanded={isMusicExpanded}
              onChange={(e) => {
                if (e.target.nodeName !== "INPUT")
                  setIsMusicExpanded(!isMusicExpanded);
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel-ambiantMusic"
                id="panel-ambiantMusic-header"
              >
                <div className={classes.heading}>
                  <GoldenSwitch
                    checked={props.exparience.isMusicActive}
                    disabled={isHandlingSong || isMultiUpload}
                    onChange={(e) => {
                      e.stopPropagation();
                      setIsHandlingSong(true);
                      myExpariencesService
                        .changeAmbiantMusicStatus(
                          props.exparience._id,
                          isAdmin,
                          e.target.checked
                        )
                        .then((res) => {
                          props.setExparience({
                            ...props.exparience,
                            isMusicActive: e.target.checked,
                          });
                          setIsHandlingSong(false);
                        })
                        .catch((err) => {
                          if (err === 403)
                            enqueueSnackbar(t("toasts.unauthorizedError"), {
                              variant: "error",
                            });
                          else
                            enqueueSnackbar(
                              t("toasts.toggleAmbiantMusicError"),
                              {
                                variant: "error",
                              }
                            );
                          setIsHandlingSong(false);
                        });
                    }}
                    label={t("configurator.ambiantMusic")}
                  />
                </div>
                <Typography className={classes.secondaryHeading}>
                  {handleAudioName(props.exparience.ambiantMusic)}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <div>
                  <AudioPlayerControlSprite />
                  <Audio
                    // string - path to audio file, required
                    src={props.exparience.ambiantMusic}
                    // string - 'none' | 'metadata' | 'auto', default: 'auto', optional
                    preload="auto"
                    // string - name for download file, optional, deafult:
                    downloadFileName={props.exparience.ambiantMusic}
                  />
                </div>
              </AccordionDetails>
              <Divider />
              <AccordionActions>
                <a
                  href={props.exparience.ambiantMusic}
                  download
                  className={classes.downloadLink}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ padding: "0 0.1rem" }}
                  >
                    {t("configurator.download")}
                  </Button>
                </a>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  component="label"
                  disabled={isHandlingSong || isMultiUpload}
                  onChange={(e) => {
                    setIsHandlingSong(true);
                    myExpariencesService
                      .uploadAmbiantMusic(
                        e.target.files,
                        props.exparience._id,
                        isAdmin
                      )
                      .then((res) => {
                        props.setExparience({
                          ...props.exparience,
                          ambiantMusic: res.ambiantMusic,
                        });
                        setIsHandlingSong(false);
                      })
                      .catch((err) => {
                        if (err === 403)
                          enqueueSnackbar(t("toasts.unauthorizedError"), {
                            variant: "error",
                          });
                        else
                          enqueueSnackbar(t("toasts.uploadAmbiantMusicError"), {
                            variant: "error",
                          });
                        setIsHandlingSong(false);
                      });
                  }}
                >
                  <input
                    type="file"
                    accept=".mp3,.wav,.ogg"
                    name="ambiantMusic"
                    hidden
                  />
                  {isHandlingSong ? (
                    <CircularProgress size={28} />
                  ) : (
                    t("configurator.importButton")
                  )}
                </Button>
              </AccordionActions>
            </Accordion>
          </div>
          <div className={classes.appearanceContainer}>
            <FormControl variant="outlined" className={classes.modifySelect}>
              <InputLabel id="appearances-select-outlined-label">
                {t("configurator.appearanceSelector")}
              </InputLabel>
              <Select
                labelId="appearances-select-outlined-label"
                id="appearances-select-outlined"
                value={props.exparience.appearance}
                onChange={(e) => {
                  myExpariencesService
                    .updateAppearanceConfig(
                      props.exparience._id,
                      e.target.value
                    )
                    .then(() => {
                      props.setExparience({
                        ...props.exparience,
                        appearance: e.target.value,
                      });
                      enqueueSnackbar(t("toasts.appearanceUpdateSuccess"), {
                        variant: "success",
                      });
                    })
                    .catch((err) => {
                      if (err === 403)
                        enqueueSnackbar(t("toasts.unauthorizedError"), {
                          variant: "error",
                        });
                      else
                        enqueueSnackbar(t("toasts.appearanceUpdateError"), {
                          variant: "error",
                        });
                    });
                }}
                label={t("configurator.appearanceSelector")}
              >
                {appearances.map((appearance) => (
                  <MenuItem key={appearance.name} value={appearance.value}>
                    {`${appearance.name}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
      </div>
    </ConfigTopOptionsContainer>
  );
};

export default ConfigTopOptions;
